import { QueryKey, queryOptions, useQueries, useQuery } from '@tanstack/react-query';

import { isOtaPlanActive } from '@/api/ota/helpers';
import { OtaPlanDetails } from '@/api/ota/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useWavelink } from '@/components/providers/WavelinkProvider';
import { OTA_DETAIL_QUERY_KEY, QUERIES_REFETCH_INTERVAL } from '@/config';
import { useOptionalSpotterContext } from '@/context/SpotterContext';
import { UseWavelinkResult } from '@/hooks/useWavelink';

export type GetDeviceOtaDetailResponse = APIResponse<OtaPlanDetails>;

const getDeviceOtaDetail = async (
    { spotterId }: { spotterId: string },
    options?: RequestOptions
): Promise<OtaPlanDetails> => {
    const response = await axios.get<GetDeviceOtaDetailResponse, GetDeviceOtaDetailResponse>(
        `/user-rest/devices/${spotterId}/ota/detail`,
        requestConfig(options)
    );

    return response.data;
};

/** The query key `executeDeviceOta` invalidates, so a started plan is picked up by every detail consumer. */
export const deviceOtaDetailQueryKey = (spotterId: string | null | undefined) => [OTA_DETAIL_QUERY_KEY, spotterId];

/**
 * The query options every OTA detail consumer shares, so all of them read and refetch one cache entry
 * per device.
 *
 * The prefix and the spotterId are their own segments, which is the shape wavelink keys an
 * invalidation message by and the shape `isQueryCovered` compares against.
 */
const deviceOtaDetailQueryOptions = (
    spotterId: string | null | undefined,
    isQueryCovered: UseWavelinkResult['isQueryCovered']
) => {
    const queryKey: QueryKey = deviceOtaDetailQueryKey(spotterId);

    return queryOptions({
        queryKey,
        // Callers enable the query only once spotterId is set, which the compiler cannot follow.
        queryFn: ({ signal }) => getDeviceOtaDetail({ spotterId: spotterId! }, { signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchInterval: isQueryCovered(queryKey) ? false : QUERIES_REFETCH_INTERVAL,
    });
};

export type UseDeviceOtaDetailOptions = {
    spotterId?: string | null;
    config?: QueryConfig<typeof getDeviceOtaDetail>;
};

/**
 * A device's most recent OTA plan, with its steps and reported state while the plan is running.
 *
 * Takes `spotterId` from the argument, or from SpotterContext when the argument is omitted. Pass
 * `spotterId: null` rather than omitting it while an explicit id is still loading, so a surrounding
 * context is not picked up by accident.
 */
export const useDeviceOtaDetail = ({
    spotterId: spotterIdProp,
    config: { enabled = true, ...config } = {},
}: UseDeviceOtaDetailOptions = {}) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: spotterIdProp });
    const { isQueryCovered } = useWavelink();

    return useQuery({
        ...deviceOtaDetailQueryOptions(spotterId, isQueryCovered),
        enabled: !!spotterId && enabled,
        ...config,
    });
};

export const useActiveOtaPlans = (spotterIds: string[]): Record<string, OtaPlanDetails> => {
    const { isQueryCovered } = useWavelink();

    return useQueries({
        queries: spotterIds.map((spotterId) => deviceOtaDetailQueryOptions(spotterId, isQueryCovered)),
        combine: (results) => {
            const activePlans: Record<string, OtaPlanDetails> = {};
            results.forEach(({ data }, index) => {
                if (isOtaPlanActive(data)) {
                    activePlans[spotterIds[index]] = data;
                }
            });
            return activePlans;
        },
    });
};
